<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import AddLabel from './AddLabel.vue';
import LabelGroupsModal from './LabelGroupsModal.vue';
import LabelGroupsAPI from 'dashboard/api/labelGroups';
import EditLabel from './EditLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showGroupsModal = ref(false);
// v83: alterna a lista de baixo entre Etiquetas (padrao) e Grupos
const listMode = ref('labels');
const inlineGroups = ref([]);
const isLoadingGroups = ref(false);

const fetchInlineGroups = async () => {
  isLoadingGroups.value = true;
  try {
    const { data } = await LabelGroupsAPI.list();
    inlineGroups.value = data.data || [];
  } catch (e) {
    inlineGroups.value = [];
  } finally {
    isLoadingGroups.value = false;
  }
};

const showGroupsList = () => {
  listMode.value = 'groups';
  fetchInlineGroups();
};

const groupLabels = group =>
  records.value.filter(l => group.label_ids.includes(l.id));

const deleteInlineGroup = async group => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir o grupo "${group.name}"? As etiquetas continuam existindo.`)) return;
  try {
    await LabelGroupsAPI.delete(group.id);
    fetchInlineGroups();
  } catch (e) {
    // silencioso
  }
};

const onGroupsModalClose = () => {
  showGroupsModal.value = false;
  if (listMode.value === 'groups') fetchInlineGroups();
};
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const searchQuery = ref('');

const records = computed(() => getters['labels/getLabels'].value);

// Super admin (user.id === 1) é o único que pode criar/editar/excluir labels.
// Demais usuários só podem listar e aplicar (taggings) em conversas.
const SUPER_ADMIN_USER_ID = 1;
const isSuperAdmin = computed(
  () => getters.getCurrentUserID.value === SUPER_ADMIN_USER_ID
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'title', weight: 4 },
    'description',
  ]);
});
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.ACTION'),
  ];
});

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        :search-placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="labels"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('LABEL_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            v-if="isSuperAdmin"
            label="Adicionar Etiquetas"
            size="sm"
            class="!w-44 justify-center"
            @click="openAddPopup"
          />
          <Button
            v-if="isSuperAdmin"
            label="Adicionar Grupos"
            size="sm"
            class="!w-44 justify-center"
            @click="showGroupsModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- v83: alternadores no MEIO da tela; o conteudo desce abaixo deles -->
      <div class="flex justify-center gap-3 mb-5">
        <Button
          label="Mostrar Etiquetas"
          size="sm"
          class="!w-44 justify-center"
          @click="listMode = 'labels'"
        />
        <Button
          label="Mostrar Grupos"
          size="sm"
          class="!w-44 justify-center"
          @click="showGroupsList"
        />
      </div>

      <!-- v83: visao GRUPOS (Mostrar Grupos) -->
      <div v-if="listMode === 'groups'" class="flex flex-col gap-3">
        <p v-if="isLoadingGroups" class="text-sm text-n-slate-11">
          Carregando grupos…
        </p>
        <p
          v-else-if="!inlineGroups.length"
          class="text-sm text-n-slate-11 py-6 text-center border border-n-weak rounded-lg"
        >
          Nenhum grupo criado ainda — use o botão "Adicionar Grupo de
          Etiquetas".
        </p>
        <div
          v-for="group in inlineGroups"
          :key="group.id"
          class="rounded-lg border border-n-weak p-4"
        >
          <div class="flex items-start justify-between gap-2 mb-2">
            <div>
              <p class="text-body-main font-medium text-n-slate-12">
                {{ group.name }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ group.label_ids.length }} etiqueta(s)
              </p>
            </div>
            <div v-if="isSuperAdmin" class="flex gap-2 shrink-0">
              <Button
                label="Editar"
                size="sm"
                variant="outline"
                @click="showGroupsModal = true"
              />
              <Button
                label="Excluir"
                size="sm"
                variant="outline"
                color="ruby"
                @click="deleteInlineGroup(group)"
              />
            </div>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="label in groupLabels(group)"
              :key="label.id"
              class="inline-flex items-center gap-1 rounded-full border border-n-weak px-2 py-0.5 text-xs text-n-slate-12"
            >
              <span
                class="inline-block w-2 h-2 rounded-full"
                :style="{ backgroundColor: label.color }"
              />
              {{ label.title }}
            </span>
            <span
              v-if="!group.label_ids.length"
              class="text-xs text-n-slate-10"
            >
              (grupo vazio)
            </span>
          </div>
        </div>
      </div>

      <BaseTable
        v-else
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery ? $t('LABEL_MGMT.NO_RESULTS') : $t('LABEL_MGMT.LIST.404')
        "
      >
        <template #row="{ items }">
          <BaseTableRow v-for="label in items" :key="label.title" :item="label">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ label.title }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ label.description }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <div class="flex items-center">
                  <span
                    class="w-4 h-4 ltr:mr-2 rtl:ml-2 border border-solid rounded border-n-weak"
                    :style="{ backgroundColor: label.color }"
                  />
                  <span class="text-body-main text-n-slate-12">
                    {{ label.color }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div
                  v-if="isSuperAdmin"
                  class="flex gap-3 justify-end flex-shrink-0"
                >
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[label.id]"
                    @click="openEditPopup(label)"
                  />
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[label.id]"
                    @click="openDeletePopup(label)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
    <LabelGroupsModal v-if="showGroupsModal" @close="onGroupsModalClose" />
</SettingsLayout>
</template>
